// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: All pads. The scratchpad's front page on the phone (More -> Scratchpad) and a
// view of the drawer on desktop: the open to-dos with a due date across every pad, the
// personal pads and the chats' shared pads as cards, a "New pad" card, and search over
// every line.

import {BoardPreview} from '@app/features/konvo_board/components/BoardPreview';
import KonvoBoards, {type BoardScope} from '@app/features/konvo_board/state/KonvoBoards';
import {dueState, formatDue} from '@app/features/konvo_scratchpad/components/ScratchpadDates';
import styles from '@app/features/konvo_scratchpad/components/ScratchpadDrawer.module.css';
import KonvoChannelScratchpad, {type ChannelPad} from '@app/features/konvo_scratchpad/state/KonvoChannelScratchpad';
import KonvoScratchpad from '@app/features/konvo_scratchpad/state/KonvoScratchpad';
import {
	type ConversationCard,
	conversationCard,
	initialsOf,
	recentConversationIds,
} from '@app/features/konvo_scratchpad/state/ScratchpadBridge';
import {
	collectDueItems,
	type DueItem,
	formatEdited,
	type PadLine,
	type PadSourceInfo,
	searchPadLines,
	summarisePad,
	toggleTodoLine,
} from '@app/features/konvo_scratchpad/state/ScratchpadOverview';
import {cleanPadName, MAX_PADS, padLabel} from '@app/features/konvo_scratchpad/state/ScratchpadPads';
import {Spinner} from '@app/features/ui/components/Spinner';
import {
	CalendarBlankIcon,
	CaretDownIcon,
	ChalkboardIcon,
	ClockIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useMemo, useRef, useState} from 'react';

// ---------------------------------------------------------------------------
// Shared pads: the chats whose pad was opened here, plus the most recent DMs and groups.

export interface SharedPadEntry {
	channelId: string;
	pad: ChannelPad;
	card: ConversationCard;
}

/** Shared pads worth showing (they have text, or are the one open now). Loads candidates once. */
export function useSharedPads(): Array<SharedPadEntry> {
	const remembered = KonvoScratchpad.sharedIds;
	const current = KonvoScratchpad.sharedChannelId;
	// Read on every render (the callers observe), so chats that arrive after mount count:
	// the Rail's Notes panel can mount before the gateway has sent any.
	const recent = recentConversationIds(6);
	const key = [...(current ? [current] : []), ...remembered, ...recent].join(',');
	const candidates = useMemo(() => [...new Set(key.split(',').filter(Boolean))], [key]);
	useEffect(() => {
		for (const id of candidates) {
			const pad = KonvoChannelScratchpad.get(id);
			if (!pad.loaded && pad.status !== 'loading' && pad.status !== 'offline') void pad.load();
		}
	}, [candidates]);
	const out: Array<SharedPadEntry> = [];
	for (const id of candidates) {
		const pad = KonvoChannelScratchpad.peek(id);
		const card = conversationCard(id);
		if (!pad || !card) continue;
		if (id !== current && pad.content.trim().length === 0) continue;
		out.push({channelId: id, pad, card});
	}
	return out;
}

/** "Lerato" for a DM (first name), the group or channel name otherwise. */
export function sharedPadName(card: ConversationCard): string {
	if (card.kind === 'dm') return card.name.split(' ')[0] || card.name;
	if (card.kind === 'channel') return `#${card.name}`;
	return card.name;
}

export function ChatAvatar({card, size = 24}: {card: ConversationCard; size?: number}) {
	return card.avatar ? (
		<img
			className={styles.chatAvatar}
			src={card.avatar}
			alt=""
			width={size}
			height={size}
			style={{width: size, height: size}}
		/>
	) : (
		<span className={styles.chatAvatar} style={{width: size, height: size, fontSize: Math.round(size * 0.38)}}>
			{card.kind === 'channel' ? '#' : initialsOf(card.name)}
		</span>
	);
}

export function AvatarStack({
	people,
	max = 3,
}: {
	people: ReadonlyArray<{id: string; name: string; avatar: string | null}>;
	max?: number;
}) {
	return (
		<span className={styles.avatarStack}>
			{people.slice(0, max).map((person) =>
				person.avatar ? (
					<img key={person.id} src={person.avatar} alt="" title={person.name} />
				) : (
					<span key={person.id} title={person.name}>
						{initialsOf(person.name)}
					</span>
				),
			)}
		</span>
	);
}

// ---------------------------------------------------------------------------

/** The top of a pad card when the pad has a board: a live picture of it. */
const BoardThumb = observer(function BoardThumb({scope}: {scope: BoardScope | null}) {
	const doc = scope ? KonvoBoards.get(scope) : null;
	useEffect(() => {
		if (doc && !doc.loaded && doc.status !== 'loading') void doc.load();
	}, [doc]);
	if (!scope || !doc || doc.isEmpty) return null;
	return (
		<span className={styles.padCardBoard} data-flx="konvo-scratchpad.home-board-thumb">
			<BoardPreview scope={scope} className={styles.padCardBoardPicture} padding={16} />
			<span className={styles.padCardBoardTag}>
				<ChalkboardIcon size={13} />
				Board
			</span>
		</span>
	);
});

function DueChip({date}: {date: Date}) {
	const state = dueState(date);
	const hot = state === 'overdue' || state === 'today';
	const label = hot
		? `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
		: formatDue(date);
	return (
		<span className={clsx(styles.dueChip, hot && styles.dueHot)}>
			{hot ? <ClockIcon size={15} weight="regular" /> : <CalendarBlankIcon size={15} weight="regular" />}
			{label}
		</span>
	);
}

function PreviewLine({line}: {line: PadLine}) {
	const todo = line.kind === 'todo' || line.kind === 'done';
	return (
		<span className={clsx(styles.cardLine, !todo && styles.cardLineText, line.kind === 'done' && styles.cardLineDone)}>
			{todo && <i className={clsx(styles.cardDot, line.kind === 'done' && styles.cardDotDone)} />}
			{line.text}
		</span>
	);
}

function NewPadCard({focusToken}: {focusToken: number}) {
	const {t} = useLingui();
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState('');
	const [error, setError] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (focusToken === 0) return;
		setEditing(true);
		cardRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
	}, [focusToken]);
	useEffect(() => {
		if (editing) inputRef.current?.focus();
	}, [editing]);
	const commit = () => {
		const name = cleanPadName(draft, KonvoScratchpad.pads);
		if (!name) {
			if (draft.trim()) setError(true);
			else setEditing(false);
			return;
		}
		if (KonvoScratchpad.addPad(name)) KonvoScratchpad.openPad(name);
		setDraft('');
		setEditing(false);
	};
	return (
		<div ref={cardRef} className={clsx(styles.padCard, styles.padCardNew)} data-flx="konvo-scratchpad.home-new-pad">
			{editing ? (
				<form
					className={styles.newPadForm}
					onSubmit={(event) => {
						event.preventDefault();
						commit();
					}}
				>
					<input
						ref={inputRef}
						className={styles.newPadInput}
						value={draft}
						maxLength={32}
						placeholder={t`Pad name`}
						aria-invalid={error}
						onChange={(event) => {
							setDraft(event.target.value);
							setError(false);
						}}
						onBlur={() => {
							if (!draft.trim()) setEditing(false);
						}}
						onKeyDown={(event) => {
							if (event.key === 'Escape') {
								event.preventDefault();
								event.stopPropagation();
								setDraft('');
								setEditing(false);
							}
						}}
						data-flx="konvo-scratchpad.home-new-pad-input"
					/>
					{error && <span className={styles.newPadError}>{t`That name is taken`}</span>}
					<button type="submit" className={styles.inkPillSmall}>
						{t`Create`}
					</button>
				</form>
			) : (
				<button type="button" className={styles.newPadButton} onClick={() => setEditing(true)}>
					<PlusIcon size={22} weight="regular" />
					<span>{t`New pad`}</span>
				</button>
			)}
		</div>
	);
}

interface ScratchpadHomeProps {
	phone: boolean;
	onClose: () => void;
}

export const ScratchpadHome = observer(function ScratchpadHome({phone, onClose}: ScratchpadHomeProps) {
	const {t} = useLingui();
	const shared = useSharedPads();
	const [query, setQuery] = useState('');
	const [searching, setSearching] = useState(KonvoScratchpad.searchOnHome);
	const [newToken, setNewToken] = useState(0);
	const searchRef = useRef<HTMLInputElement>(null);
	const loaded = KonvoScratchpad.loaded || KonvoScratchpad.status !== 'loading';

	useEffect(() => {
		if (searching) searchRef.current?.focus();
	}, [searching]);

	const pads = KonvoScratchpad.pads;
	const sources: Array<PadSourceInfo> = [
		...pads.map((pad) => ({key: `personal:${pad.name}`, label: padLabel(pad.name), content: pad.content})),
		...shared.map((entry) => ({
			key: `shared:${entry.channelId}`,
			label: sharedPadName(entry.card),
			content: entry.pad.content,
		})),
	];
	const due = collectDueItems(sources, 6);
	const hits = searching ? searchPadLines(sources, query, 40) : [];

	const openSource = (key: string, text?: string) => {
		if (key.startsWith('personal:')) KonvoScratchpad.openPad(key.slice('personal:'.length), text);
		else KonvoScratchpad.openSharedFromHome(key.slice('shared:'.length), text);
	};

	const tick = (item: DueItem) => {
		if (item.sourceKey.startsWith('personal:')) {
			const name = item.sourceKey.slice('personal:'.length);
			const pad = pads.find((entry) => entry.name === name);
			if (pad) KonvoScratchpad.updatePad(name, toggleTodoLine(pad.content, item.line.index));
			return;
		}
		const pad = KonvoChannelScratchpad.peek(item.sourceKey.slice('shared:'.length));
		if (pad) pad.setPadContent(toggleTodoLine(pad.content, item.line.index));
	};

	const now = new Date();
	return (
		<div className={styles.home} data-flx="konvo-scratchpad.home">
			<header className={styles.homeTop}>
				{phone && (
					<button
						type="button"
						className={styles.roundButton}
						aria-label={t`Close`}
						onClick={onClose}
						data-flx="konvo-scratchpad.home-close"
					>
						<CaretDownIcon size={22} weight="regular" />
					</button>
				)}
				<span className={styles.topSpacer} />
				<button
					type="button"
					className={clsx(styles.roundButton, searching && styles.roundButtonOn)}
					aria-label={t`Search pads`}
					aria-pressed={searching}
					onClick={() => {
						setSearching((value) => !value);
						setQuery('');
					}}
					data-flx="konvo-scratchpad.home-search"
				>
					<MagnifyingGlassIcon size={21} weight="regular" />
				</button>
				{pads.length < MAX_PADS && (
					<button
						type="button"
						className={clsx(styles.roundButton, styles.roundButtonInk)}
						aria-label={t`New pad`}
						onClick={() => {
							setSearching(false);
							setNewToken((value) => value + 1);
						}}
						data-flx="konvo-scratchpad.home-add"
					>
						<PlusIcon size={22} weight="regular" />
					</button>
				)}
				{!phone && (
					<button
						type="button"
						className={styles.roundButton}
						aria-label={t`Close`}
						onClick={onClose}
						data-flx="konvo-scratchpad.home-close"
					>
						<XIcon size={19} weight="regular" />
					</button>
				)}
			</header>
			<div className={styles.homeScroll}>
				{searching ? (
					<div className={styles.searchField}>
						<MagnifyingGlassIcon size={18} weight="regular" />
						<input
							ref={searchRef}
							value={query}
							placeholder={t`Search every pad`}
							onChange={(event) => setQuery(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Escape') {
									event.preventDefault();
									event.stopPropagation();
									setSearching(false);
									setQuery('');
								}
							}}
							data-flx="konvo-scratchpad.home-search-input"
						/>
						{query && (
							<button type="button" aria-label={t`Clear`} onClick={() => setQuery('')}>
								<XIcon size={15} weight="bold" />
							</button>
						)}
					</div>
				) : (
					<h1 className={styles.homeTitle}>{t`Scratchpad`}</h1>
				)}
				{!loaded ? (
					<div className={styles.loading}>
						<Spinner />
					</div>
				) : searching ? (
					query.trim() ? (
						hits.length > 0 ? (
							<div className={styles.listCard} data-flx="konvo-scratchpad.home-results">
								{hits.map((hit) => (
									<button
										type="button"
										key={`${hit.sourceKey}:${hit.line.index}`}
										className={styles.listRow}
										onClick={() => openSource(hit.sourceKey, hit.line.match)}
									>
										<span className={styles.listRowText}>
											<span className={clsx(hit.line.kind === 'done' && styles.textDone)}>{hit.line.text}</span>
											<small>{hit.sourceLabel}</small>
										</span>
									</button>
								))}
							</div>
						) : (
							<p className={styles.homeEmpty}>{t`Nothing in your pads matches “${query.trim()}”.`}</p>
						)
					) : (
						<p className={styles.homeEmpty}>{t`Search the lines of every pad, yours and shared.`}</p>
					)
				) : (
					<>
						{due.length > 0 && (
							<>
								<div className={styles.sectionLabel}>{t`Due from your pads`}</div>
								<div className={styles.listCard} data-flx="konvo-scratchpad.home-due">
									{due.map((item) => (
										<div key={`${item.sourceKey}:${item.line.index}`} className={styles.listRow}>
											<button
												type="button"
												className={styles.roundCheck}
												aria-label={t`Mark done`}
												onClick={() => tick(item)}
												data-flx="konvo-scratchpad.home-due-check"
											/>
											<button
												type="button"
												className={styles.listRowText}
												onClick={() => openSource(item.sourceKey, item.line.match)}
												data-flx="konvo-scratchpad.home-due-open"
											>
												<span>{item.line.text}</span>
												<small>{item.sourceLabel}</small>
											</button>
											<DueChip date={item.due} />
										</div>
									))}
								</div>
							</>
						)}
						<div className={styles.sectionLabel}>{t`Pads`}</div>
						<div className={styles.padGrid}>
							{pads.map((pad) => {
								const summary = summarisePad(pad.content, 4);
								const edited = KonvoScratchpad.padEditedAt(pad.name);
								return (
									<button
										type="button"
										key={`personal:${pad.name}`}
										className={styles.padCard}
										onClick={() => KonvoScratchpad.openPad(pad.name)}
										data-flx="konvo-scratchpad.home-pad"
									>
										<BoardThumb scope={KonvoScratchpad.boardScopeFor(pad.name)} />
										<b className={styles.padCardName}>{padLabel(pad.name)}</b>
										{edited && <small className={styles.padCardSub}>{t`Edited ${formatEdited(edited, now)}`}</small>}
										<span className={styles.padCardLines}>
											{summary.preview.length > 0 ? (
												summary.preview.map((line) => <PreviewLine key={line.index} line={line} />)
											) : (
												<span className={clsx(styles.cardLine, styles.cardLineText)}>{t`Empty`}</span>
											)}
										</span>
										<small className={styles.padCardFoot}>
											{summary.open + summary.done > 0
												? t`${summary.open} open · ${summary.done} done`
												: t`${summary.lines} lines`}
										</small>
									</button>
								);
							})}
							{shared.map((entry) => {
								const summary = summarisePad(entry.pad.content, 3);
								const where =
									entry.card.kind === 'dm'
										? t`Shared in your DM`
										: entry.card.kind === 'group'
											? t`Shared in the group`
											: t`Shared in #${entry.card.name}`;
								return (
									<button
										type="button"
										key={`shared:${entry.channelId}`}
										className={styles.padCard}
										onClick={() => KonvoScratchpad.openSharedFromHome(entry.channelId)}
										data-flx="konvo-scratchpad.home-shared-pad"
									>
										<BoardThumb scope={{kind: 'channel', channelId: entry.channelId}} />
										<b className={styles.padCardName}>
											<ChatAvatar card={entry.card} size={24} />
											<span>{sharedPadName(entry.card)}</span>
										</b>
										<small className={styles.padCardSub}>{where}</small>
										<span className={styles.padCardLines}>
											{summary.preview.map((line) => (
												<PreviewLine key={line.index} line={line} />
											))}
										</span>
										<small className={styles.padCardFoot}>
											{entry.card.members.length > 0 ? (
												<>
													<AvatarStack people={entry.card.members} />
													{t`${entry.card.members.length} people`}
												</>
											) : (
												t`Everyone in the channel`
											)}
										</small>
									</button>
								);
							})}
							{pads.length < MAX_PADS && <NewPadCard focusToken={newToken} />}
						</div>
					</>
				)}
			</div>
		</div>
	);
});
